// Ginja integration (platform + parent shell) for CAS portal.

export const GINJA_PARENT_URL = "https://app.sandbox.ginja.ai";
export const GINJA_PLATFORM_API_BASE = "https://api-sandbox.ginja.ai/api/v1";
export const GINJA_APP_SERVICE_API_BASE = "https://service.ginja.ai/sandbox/api/v1";

export const GINJA_COOKIE_NAME = "ginja_access_token";
export const SSO_COOKIE_SENTINEL = "sso-cookie-auth";

export const GINJA_LOCAL_STORAGE_KEYS = {
  authToken: "auth-token",
  token: "token",
  user: "user",
  userInfo: "userInfo",
  name: "name",
  role: "role",
  accessContext: "accessContext",
};

export function getEnvBearerToken() {
  // CRA exposes only REACT_APP_* env vars to the browser bundle.
  // Intended for local development only.
  const t = (process.env.REACT_APP_GINJA_BEARER_TOKEN || "").trim();
  return t || null;
}

export function redirectToParentLogin() {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${GINJA_PARENT_URL}?returnTo=${returnTo}`;
}

export function readCookie(name) {
  // Only works for non-HttpOnly cookies.
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  for (const cookie of cookies) {
    const [k, ...rest] = cookie.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}

export function consumeTokenFromHash() {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const match = hash.match(/(?:#|&)(?:token|access_token)=([^&]+)/);
  if (!match) return null;
  const token = decodeURIComponent(match[1]);
  // Strip hash after consuming.
  try {
    window.history.replaceState(
      null,
      document.title,
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {
    // no-op
  }
  return token || null;
}

function titleCase(str) {
  return (str || "")
    .toLowerCase()
    .replace(/(^|\s|_)([a-z])/g, (_, p1, p2) => `${p1}${p2.toUpperCase()}`)
    .trim();
}

const GROUP_KEY_TO_ROLE = {
  relationship_manager: "Relationship_Manager",
  operation_manager: "Operation_Manager",
  operations_manager: "Operation_Manager",
  super_admin: "Super_Admin",
};

export function deriveDisplayRole(user, context) {
  if (user?.is_privileged === true) return "";

  const groups = context?.user_groups || [];
  const matchedGroup = Array.isArray(groups)
    ? groups.find((g) => GROUP_KEY_TO_ROLE[g?.group_key])
    : null;
  if (matchedGroup) return GROUP_KEY_TO_ROLE[matchedGroup.group_key];

  const departments = context?.departments || [];
  if (Array.isArray(departments) && departments.some((d) => d?.key === "sp_management")) {
    return "SP Onboarding";
  }

  const raw = (user?.user_type || "User").toString();
  const parts = raw.split(/[\s_]+/g).filter(Boolean).map(titleCase);
  return parts.join("_") || "User";
}

export function mapGinjaAccessContextToAppUser(context) {
  const u = context?.user || {};
  const name = u?.full_name || [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim() || u?.email || "User";
  const role = deriveDisplayRole(u, context);

  // Keep this compatible with existing UI which expects `user.name` and `user.team_role[0].role`.
  return {
    name,
    email: u?.email,
    user_id: u?.user_id,
    team_role: [{ role }],
    ginja: {
      user: u,
    },
  };
}

export function mapStoredGinjaUserInfoToAppUser() {
  const rawUserInfo = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.userInfo);
  const rawAccessContext = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.accessContext);
  if (!rawUserInfo) return null;

  try {
    const user = JSON.parse(rawUserInfo);
    const accessContext = rawAccessContext ? JSON.parse(rawAccessContext) : null;
    const role = deriveDisplayRole(user, accessContext);
    const name =
      user?.full_name ||
      [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
      user?.email ||
      localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.name) ||
      "User";
    return {
      name,
      email: user?.email,
      user_id: user?.user_id,
      team_role: [{ role }],
      ginja: { user },
    };
  } catch {
    return null;
  }
}

export function clearGinjaLocalStorage() {
  Object.values(GINJA_LOCAL_STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
}

export async function fetchAccessContext(bearerHint) {
  const headers = {};
  const envToken = getEnvBearerToken();
  const tokenToUse = bearerHint || envToken;
  if (tokenToUse && tokenToUse !== SSO_COOKIE_SENTINEL) {
    headers.Authorization = `Bearer ${tokenToUse}`;
  }
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("[ginja] /me/access auth", {
      hasBearerHint: Boolean(bearerHint),
      hasEnvToken: Boolean(envToken),
      hasAuthorizationHeader: Boolean(headers.Authorization),
    });
  }

  const res = await fetch(`${GINJA_PLATFORM_API_BASE}/me/access`, {
    method: "GET",
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GET /me/access failed (${res.status}): ${text}`);
  }

  const json = await res.json();
  return json?.data || null;
}

export function persistAccessContext(authToken, context) {
  const user = context?.user || null;
  const role = deriveDisplayRole(user, context);
  const name = user?.full_name || [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.email || "";

  localStorage.setItem(GINJA_LOCAL_STORAGE_KEYS.authToken, authToken || SSO_COOKIE_SENTINEL);
  if (name) localStorage.setItem(GINJA_LOCAL_STORAGE_KEYS.name, name);
  if (role) localStorage.setItem(GINJA_LOCAL_STORAGE_KEYS.role, role);
  if (user) localStorage.setItem(GINJA_LOCAL_STORAGE_KEYS.userInfo, JSON.stringify(user));
  if (context) localStorage.setItem(GINJA_LOCAL_STORAGE_KEYS.accessContext, JSON.stringify(context));
}

export function bootstrapLegacySessionFromStorage() {
  const rawUser = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.user);
  const token = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.token);
  if (!rawUser || !token) return null;
  try {
    return JSON.parse(rawUser);
  } catch {
    return null;
  }
}

export function isAuthenticatedSession() {
  const user = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.user);
  const token = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.token);
  const authToken = localStorage.getItem(GINJA_LOCAL_STORAGE_KEYS.authToken);

  if (!user) return false;
  if (token && token !== SSO_COOKIE_SENTINEL) return true;
  if (authToken) return true; // includes sentinel for cookie-only SSO
  return false;
}

